import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Search, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_labs-ecommerce-demo/artifacts/ws3n6jq0_C%20-%20CENTURION.webp";

// Reference: Timeline shop top navigation bar (logo | nav links | search | cart)
export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  useLocation();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setMobileOpen(false);
  };

  const links = [
    { label: "Shop", to: "/" },
    { label: "Bundles", to: "/bundles", route: true },
    { label: "Why Us", to: "/standards", route: true },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#0A0F26] border-b border-white/12" data-testid="main-navbar">
      {/* Announcement marquee, electric accent bar */}
      <div className="bg-electric text-black overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 2 }).map((_, r) => (
            <div key={r} className="flex shrink-0">
              {Array.from({ length: 8 }).map((_, i) => (
                <span
                  key={i}
                  className="mx-6 py-2 text-[10px] tracking-[0.25em] uppercase font-bold font-display"
                >
                  Lab Tested · COA With Every Order · Fast Domestic Shipping · Free Ground Shipping Over $200 ·
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <nav className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <button
            className="lg:hidden text-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <img src={LOGO} alt="Century Labs" className="h-9 w-9 lg:h-10 lg:w-10 object-contain mix-blend-screen" />
            <span className="font-display text-lg lg:text-xl tracking-tighter uppercase font-bold text-white hidden sm:inline">
              Century Labs
            </span>
          </Link>

          {/* Center nav links */}
          <div className="hidden lg:flex items-center gap-9">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.label}
                  to={l.to}
                  className="text-xs tracking-[0.2em] uppercase font-bold text-white hover:text-electric transition-colors duration-200"
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.to}
                  className="text-xs tracking-[0.2em] uppercase font-bold text-white hover:text-electric transition-colors duration-200"
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </a>
              )
            )}
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-4 lg:gap-5 text-white">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Search"
              className="hover:text-electric transition-colors duration-200"
              data-testid="search-toggle"
            >
              <Search size={20} />
            </button>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open cart"
              className="relative hover:text-electric transition-colors duration-200"
              data-testid="cart-button"
            >
              <ShoppingBag size={20} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-electric text-black text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-4 border-t border-white/12 pt-4">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="SEARCH PRODUCTS..."
              className="w-full bg-transparent border border-white/20 text-white px-4 py-3 text-sm tracking-widest uppercase outline-none focus:border-electric placeholder:text-white/30 sq-sm"
              data-testid="search-input"
            />
          </form>
        )}

        {mobileOpen && (
          <div className="lg:hidden pb-5 flex flex-col gap-4 border-t border-white/12 pt-4">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm tracking-[0.2em] uppercase font-bold text-white hover:text-electric"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm tracking-[0.2em] uppercase font-bold text-white hover:text-electric"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </a>
              )
            )}
          </div>
        )}
      </nav>
    </header>
  );
};
